import "./style.scss";
import { FC, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@/src/store";
import {
  setColumnWidth,
  // toggleRowSelection,
  // toggleAllRows,
  // setActualFieldValue,
} from "@/src/store/slices/AdmissionOfficeDatabase/AODatabaseTableSlice";
import { IFiltersList, IRowData } from "@/src/types/applicationOnline";
import { IADatabaseDocument, IADatabaseFilters } from "@/src/types/admissionDatabase";
import ArrowFilter from "@/src/svg/applicationsPage/ArrowFilter";
import APFilterPopUp from "../AODFilterPopUp/AODFilterPopUp";
import VerticalLine from "../../VerticalLine/VerticalLine";
import { applicationPageRequests } from "@/src/api/applicationPageRequests";
import GuidelineSvg from "@/src/svg/sidebar/GuidelineSvg";
import { admissionDatabaseDefaultConfiguration } from "@/src/constants/AdmissionOffice/admissionOfficeDatabaseConsts";
import AODFilterPopUp from "../AODFilterPopUp/AODFilterPopUp";
import AODBDocumentActions from "./AODBDocumentActions/AODBDocumentActions";

// vocabulary id → accessor for data
const columnAccessors: Record<string, (row: IADatabaseDocument) => React.ReactNode> = {
  data_added: (row) => row.data_added,
  full_name: (row) => row.full_name,
  title: (row) => row.title,
  document_type: (row) => row.document_type,
  document_seria: (row) => row.document_seria,
  date_of_birth: (row) => row.date_of_birth,
  nationality: (row) => row.nationality,
  course: (row) => row.course,
  whose_doc: (row) => row.whose_doc,
};

interface APBodyProps {
  headerCheckboxRef: React.RefObject<HTMLInputElement | null>;
  setIsApPopUpFullName: React.Dispatch<React.SetStateAction<boolean>>;
  setCurrentDataPopUp: React.Dispatch<React.SetStateAction<IRowData | undefined>>;
  setIsCurrentPage: React.Dispatch<React.SetStateAction<string>>;

  tableFiltersList: IADatabaseFilters | undefined;
  isShowSettingsModal: boolean;
}

const AODBody: FC<APBodyProps> = ({
  headerCheckboxRef,
  setIsApPopUpFullName,
  setCurrentDataPopUp,
  setIsCurrentPage,
  tableFiltersList,
  isShowSettingsModal,
}) => {
  const dispatch = useDispatch();
  const { data, visibleColumns } = useSelector((state: RootState) => state.AODatabaseTable);
  const [filterPopUp, setFilterPopUp] = useState<string | null>(null);
  const [openTableParamMenu, setOpenTableParamMenu] = useState<string>("");
  const [openTableParamMenuRowId, setOpenTableParamMenuRowId] = useState<number | null>(null);
  // const allChecked = selectedRows.length === data.length && data.length > 0;
  // const someChecked = selectedRows.length > 0 && selectedRows.length < data.length;

  const [fixedColumnIndex, setFixedColumnIndex] = useState<number | null>(null);
  const [isAttach, setIsAttach] = useState<number | null>(null);

  const handleAttach = (index: number) => {
    if (isShowSettingsModal) {
      setFixedColumnIndex((prev) => (prev === index ? null : index));
      setIsAttach(index);
      if (isAttach === index) {
        setIsAttach(null);
      }
    }
  };

  const CHECKER_WIDTH = 4.12;

  const hasChecker = () => {
    // если вы когда-то вернёте чекер как visibleColumns[0] с id 'table-checker' — отработает
    return visibleColumns.some((col) => col.id === "table-checker");
  };

  const getStickyStyles = (index: number, section: "thead" | "tbody") => {
    if (fixedColumnIndex === null || index > fixedColumnIndex) return {};
    const checker = hasChecker() ? CHECKER_WIDTH : 0;
    const left = checker + visibleColumns.slice(0, index).reduce((acc, col) => acc + (col.width || 6), 0);
    return {
      position: "sticky" as const,
      left: `${left}rem`,
      zIndex: section === "thead" ? 7 : 5,
    };
  };

  const dragData = useRef<{ startX: number; thId: string | null; startWidth: number }>({
    startX: 0,
    thId: null,
    startWidth: 0,
  });

  const onMouseDown = (e: React.MouseEvent<HTMLDivElement>, thId: string, startWidth: number) => {
    if (!isShowSettingsModal) return;
    e.preventDefault();
    dragData.current = { startX: e.clientX, thId, startWidth };
    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
  };

  const onMouseMove = (e: MouseEvent) => {
    if (!isShowSettingsModal || !dragData.current.thId) return;
    const deltaRem = (e.clientX - dragData.current.startX) / 16;
    let newWidth = dragData.current.startWidth + deltaRem;
    if (newWidth < 2) newWidth = 2;
    dispatch(setColumnWidth({ id: dragData.current.thId, width: newWidth }));
  };

  const onMouseUp = () => {
    window.removeEventListener("mousemove", onMouseMove);
    window.removeEventListener("mouseup", onMouseUp);

    dragData.current = { startX: 0, thId: null, startWidth: 0 };
  };

  const scrollRef = useRef<HTMLDivElement>(null);
  const isDown = useRef(false);
  const startX = useRef(0);
  const scrollLeft = useRef(0);

  const handleScrollMouseDown = (e: React.MouseEvent) => {
    if (!scrollRef.current) return;
    isDown.current = true;
    startX.current = e.pageX - scrollRef.current.offsetLeft;
    scrollLeft.current = scrollRef.current.scrollLeft;
  };

  const handleScrollMouseLeave = () => (isDown.current = false);
  const handleScrollMouseUp = () => (isDown.current = false);

  const handleScrollMouseMove = (e: React.MouseEvent) => {
    if (!isDown.current || !scrollRef.current) return;
    e.preventDefault();
    const x = e.pageX - scrollRef.current.offsetLeft;
    const walkX = (x - startX.current) * 1;
    scrollRef.current.scrollLeft = scrollLeft.current - walkX;
  };

  const touchStartX = useRef(0);
  const touchScrollLeft = useRef(0);

  const handleTouchStart = (e: React.TouchEvent) => {
    if (!scrollRef.current) return;
    touchStartX.current = e.touches[0].pageX - scrollRef.current.offsetLeft;
    touchScrollLeft.current = scrollRef.current.scrollLeft;
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!scrollRef.current) return;
    const x = e.touches[0].pageX - scrollRef.current.offsetLeft;
    const walkX = (x - touchStartX.current) * 1;
    scrollRef.current.scrollLeft = touchScrollLeft.current - walkX;
  };

  const handleTableFullNameEvent = (obj: IRowData) => {
    setIsApPopUpFullName(true);
    setCurrentDataPopUp(obj);
  };

  const handleEventChangeFieldValue = async (
    event: React.MouseEvent<HTMLDivElement, MouseEvent>,
    id: number,
    value: string,
    actualPopUp: string
  ) => {
    event.stopPropagation();

    setOpenTableParamMenuRowId(null);
    setOpenTableParamMenu("");

    let actualFieldForChange: string | undefined;

    switch (actualPopUp) {
      case "star":
        actualFieldForChange = "color_star";
        break;
      case "status":
        actualFieldForChange = "form_status";
        break;
      default:
        console.warn(`Неизвестный тип actualPopUp: ${actualPopUp}`);
        return;
    }
    console.log("actualFieldForChange", actualFieldForChange);

    if (!actualFieldForChange) return;
    // dispatch(setActualFieldValue({ id, fieldActualValue: value, keyField: actualFieldForChange }));
    try {
      await applicationPageRequests.update({
        id,
        [actualFieldForChange]: value,
      });
    } catch (error) {
      console.error("Ошибка при обновлении:", error);
      alert("От халепа! Сталась помилка при зміненні поля!");
    }
  };

  const toggleTableParamMenu = (menuType: string, rowId: number) => {
    if (openTableParamMenu === menuType && openTableParamMenuRowId === rowId) {
      setOpenTableParamMenu("");
      setOpenTableParamMenuRowId(null);
    } else {
      setOpenTableParamMenu(menuType);
      setOpenTableParamMenuRowId(rowId);
    }
  };

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest(".status-menu") && !target.closest(".star-menu")) {
        setOpenTableParamMenu("");
        setOpenTableParamMenuRowId(null);
      }
    };

    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  const replacingBackendValueToCustom: Record<string, string> = {
    APPLY_ONLINE: "Apply Online",
    NEW: "New",
    // PENDING: "Pending",
  };

  return (
    <div
      className="admission-database-body"
      ref={scrollRef}
      onMouseDown={handleScrollMouseDown}
      onMouseLeave={handleScrollMouseLeave}
      onMouseUp={handleScrollMouseUp}
      style={{ overflowX: "auto" }}
    >
      <table>
        <thead>
          <tr>
            {/* <th id="table-checker" style={{ width: `${CHECKER_WIDTH}rem` }}>
              <div className="cell-wrapper">
                <label
                  className={`custom-checkbox ${someChecked && !allChecked ? "indeterminate" : ""}`}
                >
                  <input
                    type="checkbox"
                    checked={allChecked}
                    ref={headerCheckboxRef}
                    onChange={() => dispatch(toggleAllRows())}
                  />
                  <span className="checkmark"></span>
                </label>
                <div
                  className={`cell-wrapper-arrow ${filterPopUp === "color_star" ? "rotate" : ""}`}
                  onClick={() => setFilterPopUp(filterPopUp === "color_star" ? null : "color_star")}
                >
                  <ArrowFilter />
                </div>
                <div className="vertical-line">
                  <VerticalLine />
                </div>
                <APFilterPopUp
                  id="color_star"
                  tableFiltersList={tableFiltersList}
                  filterPopUp={filterPopUp}
                  setFilterPopUp={setFilterPopUp}
                />
              </div>
            </th> */}
            {visibleColumns.map(({ title, id, width }, index) => (
              <th
                key={id}
                style={{
                  width: `${width}rem`,
                  minWidth: `${width}rem`,
                  ...getStickyStyles(index, "thead"),
                }}
              >
                <div className="cell-wrapper">
                  <p>{title}</p>
                  <div className="cell-wrapper-actions">
                    {(isShowSettingsModal || isAttach === index) && (
                      <div className="cell-wrapper-attach" onClick={() => handleAttach(index)}>
                        <GuidelineSvg />
                      </div>
                    )}
                    {(id === "data_added" ||
                      id === "title" ||
                      id === "document_type" ||
                      id === "nationality" ||
                      id === "course" ||
                      id === "whose_doc") && (
                      <div
                        className={`cell-wrapper-arrow ${filterPopUp === id ? "rotate" : ""}`}
                        onClick={() => setFilterPopUp(filterPopUp === id ? null : id)}
                      >
                        <ArrowFilter />
                      </div>
                    )}
                  </div>
                  <div className="vertical-line" onMouseDown={(e) => onMouseDown(e, id, width || 6)} style={{ cursor: "ew-resize" }}>
                    <VerticalLine />
                  </div>
                  <AODFilterPopUp
                    id={id}
                    title={title}
                    tableFiltersList={tableFiltersList}
                    filterPopUp={filterPopUp}
                    setFilterPopUp={setFilterPopUp}
                    setIsCurrentPage={setIsCurrentPage}
                  />
                </div>
              </th>
            ))}
            <th
              key="document_actions"
              style={{
                width: "9rem",
                minWidth: "9rem",
                position: "sticky",
                right: 0,
                zIndex: 6,
                background: "white",
              }}
            >
              <div className="cell-wrapper">
                <p>Actions</p>
                <div className="vertical-line">
                  <VerticalLine />
                </div>
              </div>
            </th>
          </tr>
        </thead>
        <tbody onMouseMove={handleScrollMouseMove} onTouchStart={handleTouchStart} onTouchMove={handleTouchMove}>
          {data.map((row) => (
            <tr key={row.id}>
              {/* <td id="table-checker" style={{ width: `${CHECKER_WIDTH}rem` }}>
                <div className="cell-wrapper">
                  <label className="custom-checkbox">
                    <input
                      type="checkbox"
                      checked={selectedRows.includes(row.applyInfo.id)}
                      onChange={() => dispatch(toggleRowSelection(row.applyInfo.id))}
                    />
                    <span className="checkmark"></span>
                  </label>
                  <div
                    className={`star-icon ${row.applyInfo.color_star}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleTableParamMenu("star", row.applyInfo.id);
                    }}
                  >
                    {onlineApplicationsStarsConst
                      .filter((item) => row.applyInfo.color_star === item.color)
                      .map((item) => (
                        <item.Icon key={item.color} />
                      ))}
                  </div>
                  {openTableParamMenu === "star" &&
                    openTableParamMenuRowId === row.applyInfo.id && (
                      <div className="star-menu">
                        {onlineApplicationsStarsConst.map(({ color, Icon, description }) => (
                          <div
                            key={color}
                            className={`star-option ${color}`}
                            onClick={(e) =>
                              handleEventChangeFieldValue(e, row.applyInfo.id, color, "star")
                            }
                          >
                            <Icon />
                            {description && description !== "" && (
                              <div className="tooltip">{description}</div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  <div className="vertical-line">
                    <VerticalLine />
                  </div>
                </div>
              </td> */}
              {visibleColumns.map(({ id, width }, index) => {
                const rawValue = String(columnAccessors[id]?.(row) ?? "");
                const displayValue = replacingBackendValueToCustom[rawValue] ?? rawValue;
                return (
                  <td
                    key={id}
                    style={{
                      width: `${width}rem`,
                      minWidth: `${width}rem`,
                      ...getStickyStyles(index, "tbody"),
                    }}
                  >
                    <div
                      className={`cell-wrapper ${id === "full_name" ? "clicable" : ""}`}
                      // {...(id === "fullName" && { onClick: () => handleTableFullNameEvent(row) })}
                      // {...(id === "status" && {
                      //   onClick: (e) => {
                      //     e.stopPropagation();
                      //     // toggleTableParamMenu("status", row.applyInfo.id);
                      //   },
                      // })}
                    >
                      {/* {id === "status"
                        ? onlineApplicationsStatusConst
                            .filter((item) => row.applyInfo.status === item.id)
                            .map((item) => (
                              <div key={item.id} className={`status-option ${item.id}`}>
                                <span className="status-label">{item.label}</span>
                              </div>
                            ))
                        : null}
                      {id !== "status" ? <div>{displayValue}</div> : null}
                      {id === "status"
                        ? openTableParamMenu === "status" &&
                          openTableParamMenuRowId === row.applyInfo.id && (
                            <div className="status-menu">
                              {onlineApplicationsStatusConst.map(({ label, id }) => (
                                <div
                                  key={id}
                                  className={`status-option ${id}`}
                                  onClick={(e) => handleEventChangeFieldValue(e, row.applyInfo.id, id, "status")}
                                >
                                  <span className="status-label">{label}</span>
                                </div>
                              ))}
                            </div>
                          )
                        : null} */}

                      {/* {id === "document_actions" ? (
                        <AODBDocumentActions row={row} />
                      ) : ()} */}
                      <div>{displayValue}</div>
                      {/* )} */}
                      <div className="vertical-line">
                        <VerticalLine />
                      </div>
                    </div>
                  </td>
                );
              })}
              <td
                key="document_actions_td"
                style={{
                  width: "9rem",
                  minWidth: "9rem",
                  position: "sticky",
                  right: 0,
                  zIndex: 5,
                  background: "white",
                }}
              >
                <div className="cell-wrapper">
                  <AODBDocumentActions row={row} />
                  <div className="vertical-line">
                    <VerticalLine />
                  </div>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default AODBody;
